/**
 * Mensajes dinámicos para WhatsApp (Waapi).
 *
 * WhatsApp penaliza los números que envían el mismo texto una y otra vez, por eso cada mensaje se
 * arma combinando partes intercambiables: saludo según la hora (con o sin nombre), presentación del
 * comercio, frase del documento, forma de escribir el número, cierre, emoji y estructura de párrafos.
 * Además se recuerdan las últimas combinaciones usadas en esta sesión del navegador y se evita
 * repetirlas, y tampoco se repite el saludo o el cierre del mensaje anterior.
 *
 * No depende de React ni del backend: se puede usar en cualquier proyecto.
 */

/** Documentos con redacción propia. DOCUMENTO = genérico */
export type WaapiDocumento =
  | 'FACTURA'
  | 'NOTA_VENTA'
  | 'PEDIDO'
  | 'ESTADO_CUENTA'
  | 'COTIZACION'
  | 'CAJA'
  | 'RETIRO_CAJA'
  | 'DEST_ART'
  | 'COMANDA'
  | 'DOCUMENTO'

/** cliente = se agradece la compra; interno = personal del comercio (reportes de caja...) */
export type WaapiAudiencia = 'cliente' | 'interno'

export interface WaapiDocumentoInfo {
  label: string
  /** Formas de nombrar el documento dentro de una oración */
  frases: string[]
  audiencia: WaapiAudiencia
}

export const WAAPI_DOCUMENTOS: Record<WaapiDocumento, WaapiDocumentoInfo> = {
  FACTURA: {
    label: 'Factura',
    frases: [
      'su factura',
      'su factura electrónica',
      'el comprobante de su factura',
      'la factura de su compra',
    ],
    audiencia: 'cliente',
  },
  NOTA_VENTA: {
    label: 'Nota de venta',
    frases: ['su nota de venta', 'el comprobante de su compra', 'la nota de venta de su compra'],
    audiencia: 'cliente',
  },
  PEDIDO: {
    label: 'Pedido',
    frases: ['el comprobante de su pedido', 'el detalle de su pedido', 'el resumen de su pedido'],
    audiencia: 'cliente',
  },
  ESTADO_CUENTA: {
    label: 'Estado de cuenta',
    frases: ['el estado de cuenta de su consumo', 'el detalle de su consumo', 'su pre-cuenta'],
    audiencia: 'cliente',
  },
  COTIZACION: {
    label: 'Cotización',
    frases: ['la cotización solicitada', 'su cotización', 'la propuesta de precios que nos solicitó'],
    audiencia: 'cliente',
  },
  CAJA: {
    label: 'Reporte de caja',
    frases: ['el reporte de caja', 'el resumen de caja', 'el reporte del cierre de caja'],
    audiencia: 'interno',
  },
  RETIRO_CAJA: {
    label: 'Retiro de caja',
    frases: ['el comprobante de retiro de caja', 'el detalle del retiro de caja'],
    audiencia: 'interno',
  },
  DEST_ART: {
    label: 'Destino de artículos',
    frases: ['el comprobante de destino de artículos', 'el detalle del destino de artículos'],
    audiencia: 'interno',
  },
  COMANDA: {
    label: 'Comanda',
    frases: ['la comanda', 'el detalle de la comanda'],
    audiencia: 'interno',
  },
  DOCUMENTO: {
    label: 'Documento',
    frases: ['el documento solicitado', 'el documento', 'el archivo solicitado'],
    audiencia: 'cliente',
  },
}

/** Valor libre (ej. 'FACTURA' de impresión) → documento conocido o DOCUMENTO */
export const waapiDocumento = (valor?: string | null): WaapiDocumento =>
  valor && valor in WAAPI_DOCUMENTOS ? (valor as WaapiDocumento) : 'DOCUMENTO'

export interface WaapiMensajeParams {
  documento?: WaapiDocumento
  /** Reemplaza la frase del documento, ej. "el reporte de cierre de la Caja 2" */
  descripcion?: string | null
  /** Número visible del documento */
  numero?: string | number | null
  /** Nombre de quien recibe (cliente o usuario) */
  destinatario?: string | null
  /** Nombre del comercio que envía */
  empresa?: string | null
  /** Para pruebas: hora del día (0–23) */
  hora?: number
}

// ------------------------------------------------------------------ utilidades

const NOMBRES_GENERICOS = [
  'SIN NOMBRE',
  'S/N',
  'SN',
  'CLIENTE',
  'CONSUMIDOR FINAL',
  'CONTROL TRIBUTARIO',
  'VARIOS',
]

/** "JUAN PÉREZ" → "Juan Pérez"; vacío si es genérico (S/N, consumidor final...) o muy largo */
export const nombreParaSaludo = (nombre?: string | null): string => {
  const n = (nombre ?? '').trim().replace(/\s+/g, ' ')
  if (!n || n.length > 40 || NOMBRES_GENERICOS.includes(n.toUpperCase())) return ''
  return n.toLowerCase().replace(/(^|[\s'-])(\p{L})/gu, (_, sep: string, c: string) => sep + c.toUpperCase())
}

const mayuscula = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
const minuscula = (s: string) => s.charAt(0).toLowerCase() + s.slice(1)

type Azar = () => number
const indice = (n: number, azar: Azar) => Math.floor(azar() * n)

// ------------------------------------------------------------------ partes del mensaje

const saludos = (hora: number, nombre: string): string[] => {
  const c = nombre ? ` ${nombre}` : ''
  const comunes = [
    `Hola${c}`,
    `Saludos${c}`,
    `Estimado(a)${c}`,
    `Hola${c}, un gusto saludarle`,
    `Saludos cordiales${c}`,
  ]
  if (hora < 12)
    return [
      ...comunes,
      `Buenos días${c}`,
      `Hola${c}, buenos días`,
      `Muy buenos días${c}`,
      `Buen día${c}`,
      `Hola${c}, que tenga un buen día`,
      `Buen día${c}, esperamos que se encuentre bien`,
    ]
  if (hora < 19)
    return [
      ...comunes,
      `Buenas tardes${c}`,
      `Hola${c}, buenas tardes`,
      `Muy buenas tardes${c}`,
      `Hola${c}, esperamos que esté teniendo una buena tarde`,
      `Buenas tardes${c}, un gusto saludarle`,
    ]
  return [
    ...comunes,
    `Buenas noches${c}`,
    `Hola${c}, buenas noches`,
    `Muy buenas noches${c}`,
    `Buenas noches${c}, esperamos que haya tenido un buen día`,
  ]
}

const presentaciones = (empresa: string): string[] => [
  `Le escribimos de ${empresa}.`,
  `Le saludamos desde ${empresa}.`,
  `Le contactamos de ${empresa}.`,
  `Somos ${empresa}.`,
]

/** {doc} = frase del documento con su número */
const CUERPOS = [
  'le enviamos {doc}.',
  'adjuntamos {doc}.',
  'le compartimos {doc} en PDF.',
  'le hacemos llegar {doc}.',
  'le remitimos {doc}.',
  'aquí tiene {doc}.',
  'en el archivo adjunto encontrará {doc}.',
  'ponemos a su disposición {doc}.',
  'le hacemos entrega de {doc}.',
  'le facilitamos {doc}.',
  'compartimos con usted {doc}.',
  'ya está disponible {doc}; se lo adjuntamos en este mensaje.',
]

const formasNumero = (n: string): string[] => [
  `*N° ${n}*`,
  `*Nro. ${n}*`,
  `*#${n}*`,
  `número *${n}*`,
  `*${n}*`,
]

const CIERRES: Record<WaapiAudiencia, string[]> = {
  cliente: [
    '¡Muchas gracias por su preferencia!',
    'Agradecemos su compra. ¡Que tenga un excelente día!',
    'Quedamos a su disposición ante cualquier consulta.',
    'Gracias por su confianza.',
    'Muchas gracias por su compra.',
    'Cualquier duda, estamos a su disposición.',
    'Agradecemos la oportunidad de atenderle.',
    '¡Gracias por elegirnos!',
    'Si necesita alguna aclaración, no dude en escribirnos.',
    'Esperamos atenderle de nuevo muy pronto.',
    '¡Muchas gracias! Un saludo cordial.',
    'Seguimos a sus órdenes.',
  ],
  interno: [
    'Quedamos atentos a cualquier observación.',
    'Cualquier consulta, estamos atentos.',
    'Saludos cordiales.',
    'Por favor, revíselo cuando pueda.',
    'Quedamos a disposición para cualquier aclaración.',
    'Gracias por su atención.',
    'Que tenga una buena jornada.',
    'Gracias.',
  ],
}

const EMOJIS: Record<WaapiAudiencia, string[]> = {
  cliente: ['📄', '🧾', '✅', '', '', ''],
  interno: ['📊', '📄', '', '', ''],
}

// ------------------------------------------------------------------ memoria anti-repetición

const MEMORIA_MAX = 30
const MEMORIA_KEY = 'waapi-mensajes-recientes'

interface Memoria {
  firmas: string[]
  ultimoSaludo: number
  ultimoCierre: number
}

let memoria: Memoria | null = null

const leerMemoria = (): Memoria => {
  if (memoria) return memoria
  memoria = { firmas: [], ultimoSaludo: -1, ultimoCierre: -1 }
  try {
    const raw = sessionStorage.getItem(MEMORIA_KEY)
    if (raw) memoria = { ...memoria, ...JSON.parse(raw) }
  } catch {
    // sin sessionStorage (modo privado, SSR): solo memoria
  }
  return memoria!
}

const guardarMemoria = (m: Memoria) => {
  memoria = m
  try {
    sessionStorage.setItem(MEMORIA_KEY, JSON.stringify(m))
  } catch {
    // sin sessionStorage: queda en memoria
  }
}

// ------------------------------------------------------------------ generador

/**
 * Mensaje para acompañar un documento enviado por WhatsApp, distinto en cada llamada.
 * @example
 * generarMensajeWaapi({ documento: 'FACTURA', numero: 1520, destinatario: 'JUAN PEREZ', empresa: 'Café Central' })
 * // "Buenas tardes Juan Perez. Le escribimos de Café Central. Le compartimos su factura *N° 1520* en PDF.
 * //
 * //  ¡Gracias por elegirnos!"
 */
export const generarMensajeWaapi = (params: WaapiMensajeParams = {}, azar: Azar = Math.random): string => {
  const info = WAAPI_DOCUMENTOS[params.documento ?? 'DOCUMENTO'] ?? WAAPI_DOCUMENTOS.DOCUMENTO
  const nombre = nombreParaSaludo(params.destinatario)
  const empresa = (params.empresa ?? '').trim()
  const hora = params.hora ?? new Date().getHours()
  const numero = params.numero != null && `${params.numero}`.trim() ? `${params.numero}`.trim() : ''

  const listaSaludos = saludos(hora, nombre)
  const listaCierres = CIERRES[info.audiencia]
  const listaEmojis = EMOJIS[info.audiencia]
  const frases = params.descripcion?.trim() ? [params.descripcion.trim()] : info.frases
  const numeros = numero ? formasNumero(numero) : ['']
  const listaPresentaciones = empresa ? presentaciones(empresa) : []

  const m = leerMemoria()
  let elegido = ''
  let firma = ''
  let iSaludo = 0
  let iCierre = 0

  // Hasta 12 intentos de una combinación no usada recientemente
  for (let intento = 0; intento < 12; intento++) {
    iSaludo = indice(listaSaludos.length, azar)
    iCierre = indice(listaCierres.length, azar)
    if (listaSaludos.length > 1 && iSaludo === m.ultimoSaludo) iSaludo = (iSaludo + 1) % listaSaludos.length
    if (listaCierres.length > 1 && iCierre === m.ultimoCierre) iCierre = (iCierre + 1) % listaCierres.length
    const iCuerpo = indice(CUERPOS.length, azar)
    const iFrase = indice(frases.length, azar)
    const iNumero = indice(numeros.length, azar)
    const iEmoji = indice(listaEmojis.length, azar)
    // Presentación del comercio en ~la mitad de los mensajes
    const iPresentacion =
      listaPresentaciones.length && azar() < 0.5 ? indice(listaPresentaciones.length, azar) : -1
    const estructura = indice(4, azar)

    firma = [iSaludo, iCuerpo, iFrase, iNumero, iCierre, iEmoji, iPresentacion, estructura].join('.')
    if (m.firmas.includes(firma) && intento < 11) continue

    const doc = `${frases[iFrase]}${numeros[iNumero] ? ` ${numeros[iNumero]}` : ''}`
    const cuerpo = CUERPOS[iCuerpo].replace('{doc}', doc)
    const saludo = listaSaludos[iSaludo]
    const cierre = listaCierres[iCierre]
    const emoji = listaEmojis[iEmoji] ? `${listaEmojis[iEmoji]} ` : ''
    const presentacion = iPresentacion >= 0 ? `${listaPresentaciones[iPresentacion]} ` : ''

    switch (estructura) {
      case 0:
        // Saludo y cuerpo en la misma línea
        elegido = presentacion
          ? `${saludo}. ${presentacion}${emoji}${mayuscula(cuerpo)}\n\n${cierre}`
          : `${saludo}, ${emoji}${minuscula(cuerpo)}\n\n${cierre}`
        break
      case 1:
        // Saludo en su propio párrafo
        elegido = `${saludo}.\n\n${presentacion}${emoji}${mayuscula(cuerpo)}\n\n${cierre}`
        break
      case 2:
        // Todo en un párrafo
        elegido = `${saludo}. ${presentacion}${mayuscula(cuerpo)} ${cierre}`
        break
      default:
        // Cuerpo y cierre en párrafos separados, emoji al final del saludo
        elegido = `${saludo}${emoji ? ` ${emoji.trim()}` : ''}\n${presentacion}${mayuscula(cuerpo)}\n\n${cierre}`
    }
    break
  }

  guardarMemoria({
    firmas: [firma, ...m.firmas.filter((f) => f !== firma)].slice(0, MEMORIA_MAX),
    ultimoSaludo: iSaludo,
    ultimoCierre: iCierre,
  })
  return elegido.replace(/[ \t]+\n/g, '\n').trim()
}

/** Nombre del archivo que ve quien recibe, ej. "Factura 1520 - Juan Perez" (máx. 80) */
export const nombreArchivoWaapi = ({
  documento,
  numero,
  destinatario,
  titulo,
}: {
  documento?: WaapiDocumento
  numero?: string | number | null
  destinatario?: string | null
  /** Si se indica, se usa como base en lugar de la etiqueta del documento */
  titulo?: string | null
}): string => {
  const base = titulo?.trim() || WAAPI_DOCUMENTOS[documento ?? 'DOCUMENTO'].label
  const conNumero =
    numero != null && `${numero}`.trim() && !base.includes(`${numero}`) ? `${base} ${numero}` : base
  const nombre = nombreParaSaludo(destinatario)
  return (nombre ? `${conNumero} - ${nombre}` : conNumero)
    .replace(/[\\/:*?"<>|#]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80)
}
